import { t } from "i18next";
import { CloudOff, ShieldAlert } from "lucide-react";
import type { JSX } from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { OfflineRecording } from "@/lib/offline-recording/types";
import { cn } from "@/lib/utils";
import { RecordingCard } from "./RecordingCard";

/** Shown wherever recordings kept on the device are listed. */
export const SharedComputerNote = (): JSX.Element => (
  <div className="flex items-start gap-2 rounded-xl bg-muted/60 px-3 py-2.5 text-xs leading-5 text-muted-foreground">
    <ShieldAlert aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
    <p>
      {t(
        "offlineRecording.sharedComputer",
        "Recordings are kept in this browser until they upload, for at most 7 days. On a shared computer, upload or discard them and sign out before you leave.",
      )}
    </p>
  </div>
);

export interface RecordingActions {
  onProcess: (id: string) => void;
  onRetry: (id: string) => void;
  onDownload: (id: string) => void;
  onDiscard: (id: string) => void;
}

const DiscardConfirm = ({
  target,
  onCancel,
  onConfirm,
}: {
  target: OfflineRecording | null;
  onCancel: () => void;
  onConfirm: (id: string) => void;
}): JSX.Element => (
  <ConfirmDialog
    cancelText={t("offlineRecording.discard.keep", "Keep recording")}
    confirmText={t("offlineRecording.discard.confirm", "Discard recording")}
    description={
      target?.status === "uploading"
        ? t(
            "offlineRecording.discard.uploadingDescription",
            "The upload is stopped and the audio is deleted from this device. If the upload had already finished, the visit may still appear in Visits.",
          )
        : t(
            "offlineRecording.discard.description",
            "The audio is deleted from this device and cannot be recovered. Download it first if you may need it.",
          )
    }
    open={target !== null}
    title={t("offlineRecording.discard.title", "Discard this recording?")}
    onConfirm={() => {
      if (target) onConfirm(target.id);
    }}
    onOpenChange={(open) => {
      if (!open) onCancel();
    }}
  />
);

export const RecoveryDialogView = ({
  open,
  recordings,
  online,
  now,
  busyId,
  onLater,
  ...actions
}: RecordingActions & {
  open: boolean;
  recordings: OfflineRecording[];
  online: boolean;
  now?: number;
  busyId?: string | null;
  onLater: () => void;
}): JSX.Element => {
  const [discarding, setDiscarding] = useState<OfflineRecording | null>(null);
  const single = recordings.length === 1;
  return (
    <>
      <Dialog
        open={open && discarding === null}
        onOpenChange={(value) => {
          if (!value) onLater();
        }}
      >
        <DialogContent
          className={cn(
            "max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg gap-5 overflow-y-auto rounded-2xl p-5 sm:p-6",
            // A 44px close target (the shared dialog close is 16px).
            "[&>button:last-child]:right-1.5 [&>button:last-child]:top-1.5 [&>button:last-child]:flex [&>button:last-child]:h-11 [&>button:last-child]:w-11 [&>button:last-child]:items-center [&>button:last-child]:justify-center",
          )}
          data-testid="offline-recovery-dialog"
        >
          <DialogHeader className="space-y-2 pr-6 text-left">
            <DialogTitle className="text-xl">
              {single
                ? t(
                    "offlineRecording.recovery.titleOne",
                    "We kept your recording",
                  )
                : t(
                    "offlineRecording.recovery.titleMany",
                    "We kept {{count}} recordings",
                    { count: recordings.length },
                  )}
            </DialogTitle>
            <DialogDescription className="text-sm leading-6">
              {t(
                "offlineRecording.recovery.description",
                "It was not submitted (the page was closed, reloaded or interrupted), but the audio is saved on this device. Process it to create the visit, or download a copy.",
              )}
            </DialogDescription>
          </DialogHeader>
          {!online && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <CloudOff aria-hidden="true" className="h-4 w-4" />
              {t(
                "offlineRecording.recovery.offline",
                "You're offline. Processing will upload it when you're back online.",
              )}
            </p>
          )}
          <div className="flex flex-col gap-3">
            {recordings.map((recording) => (
              <RecordingCard
                key={recording.id}
                busy={busyId === recording.id}
                kind="recovered"
                now={now}
                online={online}
                recording={recording}
                onDiscard={() => setDiscarding(recording)}
                onDownload={() => actions.onDownload(recording.id)}
                onProcess={() => actions.onProcess(recording.id)}
              />
            ))}
          </div>
          <SharedComputerNote />
          <div className="flex justify-end">
            <Button
              className="min-h-11 w-full sm:w-auto"
              variant="ghost"
              onClick={onLater}
            >
              {t("offlineRecording.recovery.later", "Decide later")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <DiscardConfirm
        target={discarding}
        onCancel={() => setDiscarding(null)}
        onConfirm={(id) => {
          setDiscarding(null);
          actions.onDiscard(id);
        }}
      />
    </>
  );
};

export const PendingRecordingsList = ({
  recovered,
  queued,
  online,
  progress,
  now,
  ...actions
}: RecordingActions & {
  recovered: OfflineRecording[];
  queued: OfflineRecording[];
  online: boolean;
  progress: Record<string, number>;
  now?: number;
}): JSX.Element => {
  const [discarding, setDiscarding] = useState<OfflineRecording | null>(null);
  const empty = recovered.length === 0 && queued.length === 0;
  return (
    <div className="flex flex-col gap-5" data-testid="pending-recordings">
      {!online && !empty && (
        <p
          className="flex items-start gap-2 rounded-xl border border-warning/40 bg-warning/10 px-3 py-2.5 text-sm leading-5"
          role="status"
        >
          <CloudOff
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 shrink-0 text-warning"
          />
          {t(
            "offlineRecording.pending.offline",
            "You're offline. Recordings will upload automatically when the connection is back.",
          )}
        </p>
      )}
      {empty && (
        <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">
          <CloudOff aria-hidden="true" className="h-8 w-8 text-primary" />
          {t(
            "offlineRecording.pending.empty",
            "No recordings are waiting on this device.",
          )}
        </div>
      )}
      {recovered.length > 0 && (
        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t("offlineRecording.pending.notSubmitted", "Not submitted")}
          </h3>
          {recovered.map((recording) => (
            <RecordingCard
              key={recording.id}
              kind="recovered"
              now={now}
              online={online}
              recording={recording}
              onDiscard={() => setDiscarding(recording)}
              onDownload={() => actions.onDownload(recording.id)}
              onProcess={() => actions.onProcess(recording.id)}
            />
          ))}
        </section>
      )}
      {queued.length > 0 && (
        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t("offlineRecording.pending.uploadQueue", "Upload queue")}
          </h3>
          {queued.map((recording) => (
            <RecordingCard
              key={recording.id}
              kind="queued"
              now={now}
              online={online}
              progress={progress[recording.id]}
              recording={recording}
              onDiscard={() => setDiscarding(recording)}
              onDownload={() => actions.onDownload(recording.id)}
              onRetry={() => actions.onRetry(recording.id)}
            />
          ))}
        </section>
      )}
      <SharedComputerNote />
      <DiscardConfirm
        target={discarding}
        onCancel={() => setDiscarding(null)}
        onConfirm={(id) => {
          setDiscarding(null);
          actions.onDiscard(id);
        }}
      />
    </div>
  );
};
